import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import {
  BacktestingJobResults,
  BacktestingRunType,
  BotData,
  BotGraphType,
  FormattedChartDataPoint,
} from "@shared/types";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES, TIME_RANGE_START } from "@shared/common/constants";
import ChangeTag from "common/Tags/ChangeTag";
import Col from "antd/es/col";
import Row from "antd/es/row";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import Tabs from "common/Tabs/Tabs";
import React, { useState } from "react";
import { BotGraphTypeToDisplay } from "@shared/features/bots/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import BotChart from "features/bots/common/BotChart";
import { formatStatisticAmount } from "features/bots/BotForm/NewLook/format";
import styled, { css, useTheme } from "styled-components";
import PriceChange from "common/Charts/PriceChange";
import { segmentedTabs } from "features/bots/BotForm/NewLook/styles";
import { BacktestingGeneralStatistics } from "@shared/features/bots/BotForm/common/types";
import useStatistics from "@shared/features/bots/BotForm/Backtesting/useStatistics";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import BacktestingChart from "features/bots/BotForm/Backtesting/common/BacktestingChart";

interface GeneralTabStatisticsProps {
  backtestingRun?: BacktestingRunType;
  botData?: BotData;
  liveBarChartData?: FormattedChartDataPoint[];
  backtestingJobResults?: BacktestingJobResults;
}

const GeneralTabStatistics = ({
  backtestingRun,
  botData,
  liveBarChartData,
  backtestingJobResults,
}: GeneralTabStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { titleToValue, getChangeValue, getDisplayedValue, getHelperProps } =
    useStatistics({
      backtestingRun,
    });

  const [tab, setTab] = useState<BotGraphType>(
    BotGraphType.ACCUMULATING_PROFIT
  );

  const getStatistic = (title: BacktestingGeneralStatistics, value: number) => {
    const showChangeTag = title === BacktestingGeneralStatistics.NET_PNL;
    return (
      <Flex key={title} $flexDirection="column">
        <Text as="div" type="secondary" $size={typography.fontSizes.S}>
          {t(title)}
          <HelperTooltip {...getHelperProps(title)} />
        </Text>
        <Flex $alignItems="baseline">
          <TextWithIcon
            text={getDisplayedValue(value)}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
            style={{ alignItems: "baseline" }}
            textStyle={{ color: theme.colors.primaryText }}
            iconStyle={{ color: theme.colors.primaryText }}
          />
          {showChangeTag && (
            <ChangeTag
              value={getChangeValue(title)}
              suffix="%"
              style={{
                fontSize: typography.fontSizes.S,
              }}
              arrowStyle={{ fontSize: 8 }}
            />
          )}
        </Flex>
      </Flex>
    );
  };

  const tabsItems = [
    {
      key: BotGraphType.ACCUMULATING_PROFIT.toString(),
      label: t(BotGraphTypeToDisplay[BotGraphType.ACCUMULATING_PROFIT]),
      children: (
        <ChartWrapper $isMobile={isMobile}>
          <BacktestingChart backtestingRun={backtestingRun} />
        </ChartWrapper>
      ),
    },
    {
      key: BotGraphType.PROFIT_BY_DAY.toString(),
      label: t(BotGraphTypeToDisplay[BotGraphType.PROFIT_BY_DAY]),
      children: (
        <ChartWrapper $isMobile={isMobile}>
          <BotChart
            botId={backtestingRun?.bot_id}
            chartType={BotGraphType.PROFIT_BY_DAY}
            botData={botData}
            mobileBreakpoint={Infinity}
            isBacktesting={true}
            hideTimeRanges={true}
            defaultTimeRange={TIME_RANGE_START}
            liveChartData={
              backtestingJobResults
                ? {
                    data: liveBarChartData,
                    timeframe: backtestingRun?.backtesting_time_range,
                    currentJobTimestamp:
                      backtestingJobResults?.current_timestamp,
                  }
                : undefined
            }
          />
        </ChartWrapper>
      ),
    },
  ];

  const getNewLookStatistic = (
    title: BacktestingGeneralStatistics,
    value: number
  ) => {
    const isPnl = title !== BacktestingGeneralStatistics.INITIAL_FUNDS;
    const numericValue = Number(value) || 0;
    const sign =
      isPnl && numericValue > 0 ? "+" : isPnl && numericValue < 0 ? "−" : "";
    return (
      <NewLookStat key={title}>
        <NewLookStatLabel>
          {t(title)}
          <HelperTooltip {...getHelperProps(title)} />
        </NewLookStatLabel>
        <NewLookStatValue $value={isPnl ? numericValue : 0}>
          {sign}
          <TextWithIcon
            text={formatStatisticAmount(
              Math.abs(numericValue),
              getDisplayedValue
            )}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
            style={{ color: "inherit" }}
          />
          {title === BacktestingGeneralStatistics.NET_PNL && (
            <PriceChange
              change={Number(getChangeValue(title)) || 0}
              size={12}
              useBackground={true}
              style={{ marginLeft: 8 }}
            />
          )}
        </NewLookStatValue>
      </NewLookStat>
    );
  };

  const charts = (
    <Tabs
      activeKey={tab.toString()}
      onTabClick={(tabKey) => setTab(parseInt(tabKey))}
      size="small"
      tabBarStyle={{
        margin: "0 0 8px 0",
      }}
      $hideBorder={true}
      items={tabsItems}
    />
  );

  if (theme.isNewLook) {
    return (
      <>
        <NewLookStrip $isMobile={isMobile}>
          {Object.entries(titleToValue).map(([title, value]) =>
            getNewLookStatistic(title as BacktestingGeneralStatistics, value)
          )}
        </NewLookStrip>
        <NewLookChartTabs
          activeKey={tab.toString()}
          onTabClick={(tabKey) => setTab(parseInt(tabKey))}
          items={tabsItems}
        />
      </>
    );
  }

  return (
    <>
      <Row gutter={[0, 24]} style={{ paddingBottom: 24 }}>
        {Object.entries(titleToValue).map(([title, value]) => (
          <Col key={title} span={isMobile ? 12 : 6}>
            {getStatistic(title as BacktestingGeneralStatistics, value)}
          </Col>
        ))}
      </Row>
      {charts}
    </>
  );
};

export default GeneralTabStatistics;

const ChartWrapper = styled.div<{ $isMobile?: boolean }>`
  height: ${(p) => (p.$isMobile ? 275 : 338)}px;
`;

/* New look */
const NewLookStrip = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  column-gap: 36px;
  row-gap: 16px;
  margin-bottom: 20px;
  ${(p) =>
    p.$isMobile &&
    css`
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 20px;
    `}
`;
const NewLookStat = styled.div`
  min-width: 0;
`;
const NewLookStatLabel = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
`;
const NewLookStatValue = styled.div<{ $value: number }>`
  display: flex;
  align-items: center;
  margin-top: 4px;
  white-space: nowrap;
  font-size: 20px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$value > 0
      ? p.theme.colors.green
      : p.$value < 0
      ? p.theme.colors.red
      : p.theme.colors.titleColor};
`;
const NewLookChartTabs = styled(Tabs)`
  ${segmentedTabs};
`;
